// src/features/system/user/components/CreateUserDialog.tsx
import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import AppDialog from '@/shared/components/ui/AppDialog'
import { useUsers } from '../hooks/useUsers'
import { fetchRoles, type Role } from '@/features/system/rbac/api/rbacApi'
import { useTenants } from '@/features/system/tenants/hooks/useTenants'

interface Props {
  open: boolean
  onOpenChange: (v: boolean) => void
}

export default function CreateUserDialog({ open, onOpenChange }: Props) {
  const { create } = useUsers()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [tenantId, setTenantId] = useState<number | ''>('')
  const [roleKey,  setRoleKey ] = useState('')

  const { data: tenants = [], isLoading: tenantsLoading, isError: tenantsErr } = useTenants()
  const { data: roles = [], isLoading: rolesLoading, isError: rolesErr } = useQuery<Role[], Error>({
    queryKey: ['rbac','roles'],
    queryFn : fetchRoles,
    staleTime: 60_000,
  })

  const canSubmit =
    username.trim() &&
    password.trim() &&
    tenantId !== '' &&
    roleKey !== '' &&
    !create.isPending

  const reset = () => {
    setUsername(''); setPassword(''); setTenantId(''); setRoleKey('')
  }

  const onConfirm = async () => {
    if (!canSubmit) return
    await create.mutateAsync({
      username: username.trim(),
      password,
      tenant_id: Number(tenantId),
      roles: [roleKey],
    })
    reset()
    onOpenChange(false)
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={(v) => { if (!v) reset(); onOpenChange(v) }}
      title="Create new user"
      size="md"
      // use your richer footer API:
      confirmLabel={create.isPending ? 'Creating…' : 'Create user'}
      confirmDisabled={!canSubmit}
      isBusy={create.isPending}
      lockOnBusy
      onConfirm={onConfirm}
      cancelLabel="Cancel"
    >
      {/* ——— form body (unchanged) ——— */}
      <label className="block text-sm text-neutral-300">
        Username
        <input
          autoFocus
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="jdoe"
          className="mt-1 w-full bg-neutral-800 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </label>

      <label className="block text-sm text-neutral-300">
        Password
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          className="mt-1 w-full bg-neutral-800 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </label>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block text-sm text-neutral-300">
          Tenant
          <select
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value ? Number(e.target.value) : '')}
            className="mt-1 w-full bg-neutral-800 rounded px-3 py-2"
          >
            <option value="">— select tenant —</option>
            {tenantsLoading && <option disabled>Loading…</option>}
            {tenantsErr && <option disabled>Failed to load tenants</option>}
            {!tenantsLoading && !tenantsErr && tenants.map(t => (
              <option key={t.id_tenant} value={t.id_tenant}>
                {t.display_name ?? t.code ?? `#${t.id_tenant}`}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm text-neutral-300">
          Role
          <select
            value={roleKey}
            onChange={(e) => setRoleKey(e.target.value)}
            className="mt-1 w-full bg-neutral-800 rounded px-3 py-2"
          >
            <option value="">— select role —</option>
            {rolesLoading && <option disabled>Loading…</option>}
            {rolesErr && <option disabled>Failed to load roles</option>}
            {!rolesLoading && !rolesErr && roles.map(r => (
              <option key={r.id_role} value={r.role_key}>
                {r.display_name ?? r.role_key}
              </option>
            ))}
          </select>
        </label>
      </div>

      {create.isError && (
        <p className="text-sm text-rose-400">
          {(create.error as Error)?.message ?? 'Could not create user.'}
        </p>
      )}
    </AppDialog>
  )
}
